
<template v-if="action">
  <tr
    class="calculation-action-row"
    :class="{
      'has-generators': action.linkedGenerators?.length,
      'has-errors': action.errors?.length,
    }"
  >
    <!-- Name + chevron + generator color -->
    <td :class="{ 'pl-10': indent }" @click="editAction(action)">
      <span class="action-inline">
        <!-- Chevron (only when there are linked generators) -->
        <span
          v-if="action.linkedGenerators?.length"
          class="chev"
          @click.stop="emitToggleAction(action.id)"
        >
          <v-icon size="16">
            {{ !toggledAction?.includes(action.id) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
          </v-icon>
        </span>

        <!-- Generator color dot (only when action.isLinkedGenerator) -->
        <span v-if="action.isLinkedGenerator">
          <span
            class="generator-color-dot"
            :style="{ backgroundColor: genColor }"
            :title="String(action.id)"
          ></span>
        </span>

        <span class="action-name" @click="editAction(action)">
          {{ action.name }}
        </span>
      </span>
    </td>

    <!-- Method text + errors tooltip -->
    <td @click="editAction(action)">
      <v-chip
        v-if="methodText"
        size="small"
        color="secondary"
        variant="tonal"
        class="method-chip"
      >
        {{ methodText }}
      </v-chip>

      <span v-if ="action && action.errors"> 
        <v-tooltip v-if="action.errors?.length" location="bottom" color="error">
          <template #activator="{ props }">
            <v-icon v-bind="props" class="ml-2" color="error">mdi-alert</v-icon>
          </template>

          <div v-for="err in action.errors" :key="err">
            {{ err }}<br />
          </div>
        </v-tooltip>
      </span>
    </td>

    <!-- Energiekosten (with small breakdown tooltip) -->
    <td class="text-right" @click="editAction(action)">
      <v-tooltip location="top" color="white">
        <template #activator="{ props }">
          <span v-bind="props">
            <template v-if="showPercentages">
              {{ fmtPercent(action.energyCostSavingsPercentage ?? 0) }}
            </template>
            <template v-else>
              {{ fmt(action.energyCostSavings) }}
            </template>
          </span>
        </template>

        <v-table density="compact">
          <tbody>
            <tr>
              <th class="text-left">Energiekosten:&nbsp;</th>
              <td class="text-right">
                <template v-if="showPercentages">
                  {{ fmtPercent(action.energyCostSavingsPercentage ?? 0) }}
                </template>
                <template v-else>
                  {{ fmt(action.energyCostSavings) }}
                </template>
              </td>
            </tr>
            <tr>
              <th class="text-left">Instandhaltungskosten:&nbsp;</th>
              <td class="text-right">
                {{ fmt(-(action.deltaOperationalCost ?? 0)) }} €
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-tooltip>
    </td>

    <!-- Einspeisung -->
    <td class="text-right" @click="editAction(action)" v-if="showFeedInColumn">
      <template v-if="showPercentages">
        {{ fmtPercent(action.feedInProfitPercentage ?? 0) }}
      </template>
      <template v-else>
        {{ fmt(action.feedInProfit) }}
      </template>
    </td>

    <!-- Wartungskosten. `deltaOperationalCost` ist plan minus Bestand, eine
         Ersparnis also negativ. Die Tabelle weist durchweg Ersparnisse aus —
         Energiekosten, Einspeisung, CO₂ —, deshalb negiert. Das ist auch das
         Vorzeichen, das `deltaOperationalCostPercentage` schon immer trug:
         vorher drehte der Prozentschalter das Vorzeichen dieser einen Zelle. -->
    <td class="text-right" @click="editAction(action)">
      <template v-if="showPercentages">
        {{ fmtPercent(action.deltaOperationalCostPercentage ?? 0) }}
      </template>
      <template v-else>
        {{ fmt(-(action.deltaOperationalCost ?? 0)) }}
      </template>
    </td>

    <!-- CO₂ -->
    <td class="text-right" @click="editAction(action)">
      <template v-if="showPercentages">
        {{ fmtPercent(action.co2SavingsPercentage ?? 0) }}
      </template>
      <template v-else>
        {{ fmt((action.co2Savings || 0) / 1000) }}
      </template>
    </td>

    <!-- Invest -->
    <td class="text-right" @click="editAction(action)">
      {{ fmt(action.invest) || '-' }}
    </td>

    <!-- Energies -->
    <td
      class="text-right"
      v-for="eid in energies"
      :key="'energy-' + eid"
      @click="editAction(action)"
    >
      <template v-if="!action.errors?.length">
        <template v-if="action.energySavings && action.energySavings[eid] != null">
          <template v-if="showPercentages">
            {{ fmtPercent(action.energySavingsPercentage?.[eid] ?? 0) }}
          </template>
          <template v-else>
            {{ fmt(action.energySavings[eid]) }}
          </template>
        </template>
        <template v-else>-</template>
      </template>
    </td>

    
    <!-- Buttons -->
    <td class="actions-cell text-center sticky-report-column">
      <div class="actions">
        <v-btn
          :disabled="(action.errors?.length || 0) !== 0"
          :href="docxDownloadHref"
          color="primary"
          variant="text"
          size="small"
          density="comfortable"
          :ripple="false"
          icon
          title="Kurzbericht dieser Maßnahme als Word Export"
          @click.stop
        >
          <v-icon size="20">mdi-file-word</v-icon>
        </v-btn>

        <v-btn
          icon
          variant="text"
          size="small"
          density="comfortable"
          :ripple="false"
          title="Maßnahme bearbeiten"
          @click.stop="editAction(action)"
        >
          <v-icon size="20">mdi-pencil</v-icon>
        </v-btn>
      </div>
    </td>

  </tr>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useProjectStore } from '@/stores/projectStore'

/* ---------------------------------------
 * Props
 * ------------------------------------- */
const props = defineProps({
  action: { type: Object, required: true },
  energies: { type: Array, required: true },
  indent: { type: Boolean, default: false },
  toggledAction: { type: Array, default: () => [] },
  methodTexts: { type: Object, default: null },
  showPercentages: { type: Boolean, default: false },
  showFeedInColumn: { type: Boolean, default: false },
})

const emit = defineEmits(['toggleAction'])

/* ---------------------------------------
 * Router & Store
 * ------------------------------------- */
const router = useRouter()
const store = useProjectStore()

/* ---------------------------------------
 * Formatters (modern)
 * ------------------------------------- */
const numFmt = new Intl.NumberFormat('de-DE')
const pctFmt = new Intl.NumberFormat('de-DE', {
  style: 'percent',
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
})

const fmt = (v) => {
  const n = Number(v ?? 0)
  return numFmt.format(Number.isFinite(n) ? n : 0)
}

const fmtPercent = (fraction) => {
  const f = Number(fraction ?? 0)
  return pctFmt.format(Number.isFinite(f) ? f : 0)
}

/* ---------------------------------------
 * Derived
 * ------------------------------------- */
const methodText = computed(() => {
  if (!props.methodTexts) return ''
  const actionType = props.action?.type
  if (!actionType) return ''
  return props.methodTexts[actionType] || ''
})

const docxDownloadHref = computed(
  () => `/api/v1/actions/${props.action?.id}/action_report/`
)


const genColor = computed(() => {
  const id = props.action?.id
  return (id != null && store.genColors?.[id]) ? store.genColors[id] : '#888'
})

/* ---------------------------------------
 * Methods
 * ------------------------------------- */
function editAction(action) {
  router.push({
    name: 'Action',
    params: { actionId: action.id },
  })
}

function emitToggleAction(id) {
  emit('toggleAction', id)
}
</script>

<style scoped lang="scss">
.calculation-action-row {
  cursor: pointer;
  transition: background-color 160ms ease;
}

.calculation-action-row:hover {
  background-color: rgba(var(--v-theme-primary), 0.055);
}

.calculation-action-row.has-generators {
  font-weight: 600;
}

.calculation-action-row.has-errors {
  background-color: rgba(var(--v-theme-error), 0.035);
}

/* ========================================
   Name and generator indicator
======================================== */

.action-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.chev {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  width: 24px;
  height: 24px;

  border-radius: 999px;

  color: rgb(var(--v-theme-primary));
  transition: background-color 160ms ease;
}

.chev:hover {
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.generator-color-dot {
  display: inline-block;
  flex-shrink: 0;

  width: 11px;
  height: 11px;
  border-radius: 999px;

  box-shadow: 0 0 0 2px rgb(var(--v-theme-surface));
}

.action-name {
  max-width: 280px;
  overflow: hidden;

  user-select: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.method-chip {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ========================================
   Horizontally sticky action column

   This cell remains visible while scrolling
   left/right, but moves normally up/down.
======================================== */

td.actions-cell.sticky-report-column {
  position: sticky !important;
  right: 0 !important;

  /* Explicitly disable vertical stickiness */
  top: auto !important;
  bottom: auto !important;

  /*
   * Lower than:
   * - sticky header: 40
   * - sticky footer: 30
   * - footer/right intersection: 35
   */
  z-index: 10 !important;

  width: 96px;
  min-width: 96px;
  max-width: 96px;

  padding-inline: 6px !important;
  vertical-align: middle;

  background-color: rgb(var(--v-theme-surface)) !important;
}

/* Match the row hover color */
.calculation-action-row:hover td.actions-cell.sticky-report-column {
  background:
    linear-gradient(
      rgba(var(--v-theme-primary), 0.055),
      rgba(var(--v-theme-primary), 0.055)
    ),
    rgb(var(--v-theme-surface)) !important;
}

/* Match the error-row color */
.calculation-action-row.has-errors td.actions-cell.sticky-report-column {
  background:
    linear-gradient(
      rgba(var(--v-theme-error), 0.035),
      rgba(var(--v-theme-error), 0.035)
    ),
    rgb(var(--v-theme-surface)) !important;
}

/* ========================================
   Action buttons
======================================== */

.actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;

  width: 100%;
}

.actions .v-btn.v-btn--icon {
  width: 34px;
  min-width: 34px;
  height: 34px;

  padding: 0;
  border-radius: 999px;
}

.actions .v-btn:hover {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* ========================================
   Small screens
======================================== */

@media (max-width: 700px) {
  .action-name {
    max-width: 210px;
  }

  .method-chip {
    max-width: 180px;
  }

  td.actions-cell.sticky-report-column {
    width: 88px;
    min-width: 88px;
    max-width: 88px;

    /*
     * Retain horizontal-only stickiness.
     */
    top: auto !important;
    bottom: auto !important;
  }

  .actions {
    gap: 0.15rem;
  }

  .actions .v-btn.v-btn--icon {
    width: 32px;
    min-width: 32px;
    height: 32px;
  }
}
</style>
